import React from 'react';
import classNames from 'classnames';
import { Trans } from 'react-i18next';
import { AlertCircle } from 'react-feather';
import { AIStatus, AIError } from '@/hooks/useAIAssist';
import { TodesktopLink } from '@/components/Desktop';

type Props = {
    status: AIStatus;
    error?: AIError;
    className?: string;
    variant?: 'default' | 'minimal';
};

const AIStatusBanner = React.memo(({ status, error, className, variant = 'default' }: Props) => {
    if (status !== 'error') {
        return null;
    }

    let translationKey: string | null = null;
    let bgColor = 'bg-red-50 dark:bg-red-900/20';
    let textColor = 'text-saga-light-red';

    switch (error) {
        case 'promptLength':
            translationKey = 'ai.too_many_prompt_tokens';
            break;
        case 'rateLimit':
            translationKey = 'ai.too_many_requests';
            break;
        default:
            translationKey = 'ai.issue_with_generating';
    }

    return (
        <div
            className={classNames(
                'flex items-center justify-start transition-all duration-200 ease-in-out',
                variant === 'default' && 'mx-auto px-4 py-2 rounded-t-lg',
                variant === 'minimal' && 'py-1',
                variant === 'default' && bgColor,
                className,
                'transform transition-transform duration-200',
            )}
            style={variant === 'default' ? { maxWidth: 'calc(100% - 16px)' } : undefined}
        >
            <AlertCircle size={14} className={classNames('mr-2 flex-shrink-0', textColor)} />
            <div className={classNames('text-sm', textColor)}>
                <Trans
                    i18nKey={translationKey}
                    components={{
                        1: (
                            <TodesktopLink
                                path="https://saga.so/guides/saga-ai-faqs"
                                className="underline cursor-pointer"
                            />
                        ),
                    }}
                />
            </div>
        </div>
    );
});

AIStatusBanner.displayName = 'AIStatusBanner';

export default AIStatusBanner;
